/* eslint-disable @typescript-eslint/no-shadow */
import clsx from 'clsx';

import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';

import { Card as CardsMini } from 'components/slider/cards/mini';
import { Card as CardsMedium } from 'components/slider/cards/medium';
import {
  ICasino,
  IProvider,
  ISlot,
  ICasinoReview,
  IBlogPost,
  IStreamerReview,
  IStreamer,
} from 'constants/types';

import { Header } from 'components/slider/header';

import { Icon } from 'common/icon';
import styles from './styles.module.css';
import { SeeMoreCard } from 'components/slider/cards/see-more-card';
import { ProviderCard } from 'components/template/providers/collection-providers';
import { ReviewsCard } from 'components/cards';
import { useLoginModal } from 'hooks';
import { LoginModal } from 'components/modal/login';
import { NewsCard } from 'components/template/guides-category/card';
import { StreamerCard } from 'components/streamer-card';

const DEFAULT_EMBLA_OPTIONS: EmblaOptionsType = {
  align: 'start',
  dragFree: false,
  startIndex: 0,
};

type PropType = {
  headerId?: string;
  url?: string;
  type?:
    | 'casino'
    | 'slot'
    | 'provider'
    | 'reviews'
    | 'blog-and-news'
    | 'guide'
    | 'streamers';
  description?: string;
  name?: string;
  slides:
    | ICasino[]
    | ISlot[]
    | IProvider[]
    | ICasinoReview[]
    | IBlogPost[]
    | IStreamerReview[]
    | IStreamer[];
  count?: number | string;
  options?: EmblaOptionsType | undefined;
  ratings?: string[];
  isLinkTitle?: boolean;
};

type PropTypeArrow = PropsWithChildren<
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  >
>;

export const PrevButton = (props: PropTypeArrow) => {
  const { children, ...restProps } = props;

  return (
    <button
      aria-label="Back"
      className={clsx(styles.button, styles.button__prev)}
      type="button"
      {...restProps}
    >
      <Icon.ArrowSlider />
      {children}
    </button>
  );
};

export const NextButton = (props: PropTypeArrow) => {
  const { children, ...restProps } = props;

  return (
    <button
      aria-label="Next"
      className={clsx(styles.button, styles.button__next)}
      type="button"
      {...restProps}
    >
      <Icon.ArrowSlider />
      {children}
    </button>
  );
};

export const CardsMediumCarousel = (props: PropType): React.JSX.Element => {
  const {
    headerId,
    slides,
    options,
    name,
    count,
    type,
    url,
    description,
    isLinkTitle,
  } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel(
    options ?? DEFAULT_EMBLA_OPTIONS
  );
  const {
    handleCloseLoginModal,
    handleConfirmLoginModal,
    handleVisibleLoginModal,
    isLoginVisible,
  } = useLoginModal();
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi]
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi]
  );

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    const nextPrevDisabled = !emblaApi.canScrollPrev();
    const nextNextDisabled = !emblaApi.canScrollNext();

    setPrevBtnDisabled((current) =>
      current === nextPrevDisabled ? current : nextPrevDisabled
    );
    setNextBtnDisabled((current) =>
      current === nextNextDisabled ? current : nextNextDisabled
    );
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect]);

  const seeMoreType = useMemo(() => {
    if (type === 'reviews') {
      return 'big';
    }
    if (type === 'casino') {
      return 'medium';
    }
    if (type === 'slot') {
      return 'small';
    }
    if (type === 'provider') {
      return 'box';
    }
    if (type === 'streamers') {
      return 'high';
    }
  }, [type]);

  if (!slides?.length) {
    return <></>;
  }

  return (
    <>
      <section className={styles.container}>
        <div className={styles.header}>
          <Header
            id={headerId}
            name={name}
            count={count}
            hasIcon={isLinkTitle}
            url={url?.length ? url : type === 'casino' ? 'casinos' : 'slots'}
          />
        </div>
        {description && <div className={styles.description}>{description}</div>}
        <div className={styles.embla}>
          <div
            className={clsx(styles.background_left, {
              [styles.background_hidden]: prevBtnDisabled,
              [styles.background_slot_height]:
                type === 'slot' || type === 'provider',
            })}
          />
          <div className={styles.embla__viewport} ref={emblaRef}>
            <div className={styles.embla__container}>
              {slides?.map((item, index) => {
                switch (type) {
                  case 'casino':
                    return (
                      <CardsMedium
                        key={item?._id + index}
                        data={item as ICasino}
                        isWidthStretching
                      />
                    );
                  case 'slot':
                    return (
                      <CardsMini
                        key={item?._id + index}
                        data={item as ISlot}
                        isWidthStretching
                      />
                    );
                  case 'provider':
                    return (
                      <ProviderCard
                        key={item?._id?.toString()}
                        data={item as IProvider}
                        isWidthStretching
                      />
                    );
                  case 'reviews':
                    return (
                      <ReviewsCard
                        handleLoginModal={handleVisibleLoginModal}
                        isSlider
                        rating={
                          (item as ICasinoReview)?.rating?.toString() || '0'
                        }
                        comment={item as ICasinoReview}
                        key={item?._id?.toString()}
                      />
                    );
                  case 'blog-and-news':
                    return (
                      <NewsCard
                        type={type}
                        data={item as IBlogPost}
                        key={item?._id?.toString()}
                      />
                    );
                  case 'guide':
                    return (
                      <NewsCard
                        type={type}
                        data={item as IBlogPost}
                        key={item?._id?.toString()}
                      />
                    );
                  case 'streamers':
                    return (
                      <StreamerCard
                        key={item?._id + index}
                        item={item as IStreamer}
                        variant={'main'}
                        isLink
                      />
                    );

                  default:
                    return null;
                }
              })}

              {Number(count ?? 0) > 10 && type !== 'reviews' && (
                <SeeMoreCard
                  count={Number(count) - 10}
                  url={
                    url?.length ? url : type === 'casino' ? 'casinos' : 'slots'
                  }
                  type={seeMoreType}
                />
              )}
            </div>
          </div>

          <div className={styles.swiper_btn_prev}>
            <PrevButton onClick={scrollPrev} disabled={prevBtnDisabled} />
          </div>
          <div className={styles.swiper_btn_next}>
            <NextButton onClick={scrollNext} disabled={nextBtnDisabled} />
          </div>

          <div
            className={clsx(styles.background_right, {
              [styles.background_hidden]: nextBtnDisabled,
              [styles.background_slot_height]:
                type === 'slot' || type === 'provider',
            })}
          />
        </div>
        <LoginModal
          isVisible={isLoginVisible}
          onClose={handleCloseLoginModal}
          confirmOnClick={handleConfirmLoginModal}
        />
      </section>
    </>
  );
};
